import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AGENCY_LINK_ROLE_LABELS, formatToLocal } from '@safetyhub/shared';
import { Badge, Card } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { usePermissions } from '../../hooks/usePermissions';
import { apiErrorMessage } from '../../lib/api';
import { agencySiteLinkApi, agencySiteLinkQueryKeys } from '../../lib/agencySiteLink';
import { REQUEST_STATUS_LABEL, agencyLinkRequestPath, requestSiteLabel } from '../../lib/agencySiteLinkView';

type Direction = 'incoming' | 'outgoing';

/**
 * 대행 연결 요청 목록(P4-1) — 「받은 요청」(업체: 승인·거절할 것) · 「보낸 요청」(기관: 취소할 것). 대기 중인 것만 보인다
 * (서버 기본 `status=PENDING`). 한 건을 누르면 `/agency-link-requests/:requestId`에서 처리한다.
 * 받은 요청 탭은 업체 OWNER에게, 보낸 요청 탭은 `site.manage`에게 — 서버 권한과 같은 축이다.
 */
export default function AgencyLinkRequestsPage() {
  const organizationId = useAuthStore((s) => s.currentMembership?.companyId);
  const { isOwner, canManageSites } = usePermissions();
  const [direction, setDirection] = useState<Direction>(isOwner ? 'incoming' : 'outgoing');

  const { data, isLoading, error } = useQuery({
    queryKey: agencySiteLinkQueryKeys.requests(organizationId ?? '', direction, 'PENDING'),
    queryFn: () => agencySiteLinkApi.listRequests(organizationId!, direction),
    enabled: !!organizationId,
  });

  const tabs: Array<{ key: Direction; label: string; visible: boolean }> = [
    { key: 'incoming', label: '받은 요청', visible: isOwner },
    { key: 'outgoing', label: '보낸 요청', visible: canManageSites },
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/sites" className="text-sm text-ink-600 hover:text-ink-900">
          ← 현장 목록으로
        </Link>
        <h1 className="mt-2 text-[26px] font-extrabold tracking-tight text-ink-900">대행 연결 요청</h1>
        <p className="mt-1 text-[15px] font-medium text-ink-500">승인을 기다리는 대행 기관 연결 요청입니다.</p>
      </div>

      <div className="border-b border-line">
        <nav className="-mb-px flex space-x-8">
          {tabs
            .filter((t) => t.visible)
            .map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setDirection(t.key)}
                className={`border-b-2 px-1 pb-3 text-sm font-medium ${
                  direction === t.key
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-ink-500 hover:border-ink-300 hover:text-ink-700'
                }`}
              >
                {t.label}
              </button>
            ))}
        </nav>
      </div>

      <Card padding="none" radius="lg" elevation="md">
        {isLoading ? (
          <p className="p-6 text-sm text-ink-500">불러오는 중...</p>
        ) : error ? (
          <p className="p-6 text-sm text-rose-600">{apiErrorMessage(error) ?? '요청 목록을 불러오지 못했습니다.'}</p>
        ) : !data || data.requests.length === 0 ? (
          <p className="p-6 text-sm text-ink-500">대기 중인 요청이 없습니다.</p>
        ) : (
          <ul className="divide-y divide-line">
            {data.requests.map((r) => (
              <li key={r.requestId}>
                <Link
                  to={agencyLinkRequestPath(r.requestId)}
                  className="flex items-center justify-between gap-3 px-6 py-4 hover:bg-surface-hover"
                >
                  <div>
                    <p className="text-sm font-medium text-ink-900">{requestSiteLabel(r)}</p>
                    <p className="text-xs text-ink-500">
                      {direction === 'incoming' ? r.agency.name : r.target.name} · {AGENCY_LINK_ROLE_LABELS[r.role]} ·{' '}
                      {formatToLocal(new Date(r.requestedAt))}
                    </p>
                  </div>
                  <Badge tone="amber" size="sm">
                    {REQUEST_STATUS_LABEL[r.status]}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
